import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from '@/components/business/icon'
import { cn } from '@/lib/utils'

export interface IdentityDetailItem {
  label: string
  value: string
}

interface ExpandableIdentityDetailsProps {
  summary: string
  details?: IdentityDetailItem[]
  align?: 'start' | 'center'
  className?: string
}

export function ExpandableIdentityDetails({
  summary,
  details,
  align = 'center',
  className,
}: ExpandableIdentityDetailsProps) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const detailsId = useId()
  const hasDetails = Boolean(details?.length)

  useEffect(() => {
    if (!detailsOpen) return

    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setDetailsOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDetailsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [detailsOpen])

  if (!hasDetails) {
    return (
      <p className={cn('max-w-full truncate text-[12px] leading-4 text-[#707070]', className)}>
        {summary}
      </p>
    )
  }

  return (
    <div ref={containerRef} className={cn('relative max-w-full', className)}>
      <button
        type="button"
        aria-expanded={detailsOpen}
        aria-controls={detailsId}
        onClick={() => setDetailsOpen((open) => !open)}
        className="focus-visible:ring-primary/25 flex max-w-full items-center gap-0.5 rounded-full px-1 text-[12px] leading-4 text-[#707070] transition-colors hover:text-[#3a3a3c] focus-visible:ring-2 focus-visible:outline-none"
      >
        <span className="truncate">{summary}</span>
        <Icon
          name="expand_more"
          className={detailsOpen ? 'shrink-0 rotate-180 text-[14px]' : 'shrink-0 text-[14px]'}
        />
      </button>

      {detailsOpen && (
        <div
          id={detailsId}
          role="region"
          aria-label="律师执业信息"
          className={cn(
            'glass-panel absolute top-full z-50 mt-2 w-max max-w-[min(21rem,calc(100vw-2rem))] rounded-2xl border px-4 py-3 text-left shadow-[0_16px_40px_rgba(15,23,42,0.14)]',
            align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0',
          )}
        >
          <dl className="space-y-2">
            {details?.map((item) => (
              <div
                key={`${item.label}-${item.value}`}
                className="grid grid-cols-[auto_minmax(0,1fr)] gap-2"
              >
                <dt className="text-on-surface-variant text-xs whitespace-nowrap">{item.label}</dt>
                <dd className="text-on-surface text-xs leading-5 break-words">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  )
}
